import { Business as BusinessIcon } from "@mui/icons-material";
import { Avatar, Box, Button, CircularProgress, Grid, Typography } from "@mui/material";
import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { RenderStates, type RenderStatesProps } from "~ui/atoms/RenderStates/RenderStates";
import { EntityListSearchField } from "~ui/molecules/EntityListSearchField/EntityListSearchField";

const tNoSearchMatch = "No districts match your search.";

type GridItemSize = number | { lg?: number; md?: number; sm?: number; xl?: number; xs?: number };

export interface District {
  id: string;
  bubbleId: string;
  name: string;
  avatarUri?: string;
}

export type DistrictsTemplateBaseProps = Pick<RenderStatesProps<District[]>, "data" | "status">;

export type DistrictsTemplateProps = DistrictsTemplateBaseProps & {
  itemSize?: GridItemSize;
  showSearch?: boolean;
};

const districtListContent = (district: District) => (
  <Button
    component={RouterLink}
    sx={{
      borderRadius: "4px",
      color: "inherit",
      justifyContent: "flex-start",
      padding: {
        md: "1rem",
        sm: "0.75rem",
        xs: "0.5rem",
      },
      textAlign: "left",
      textTransform: "none",
      width: "100%",
    }}
    to={`/admin?adminSelect=${district.bubbleId}`}
    variant="text"
  >
    <Grid alignItems="center" container direction="row" gap={{ md: "2.5rem", sm: "1.5rem", xs: "1rem" }}>
      <Grid alignItems="center" container direction="row" size="grow">
        <Grid>
          <Avatar
            slotProps={{
              img: {
                "aria-label": `${district.name} district avatar`,
              },
            }}
            src={district.avatarUri}
            sx={{
              height: { md: "3.5rem", sm: "3rem", xs: "2.5rem" },
              width: { md: "3.5rem", sm: "3rem", xs: "2.5rem" },
            }}
          >
            <BusinessIcon />
          </Avatar>
        </Grid>
        <Grid marginLeft={{ md: "1em", sm: "0.75em", xs: "0.5em" }}>
          <Typography
            component="p"
            sx={{
              fontSize: { md: "1.25rem", sm: "1rem", xs: "0.9rem" },
            }}
            variant="h6"
          >
            {district.name}
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  </Button>
);

export const DistrictsTemplate = ({
  data,
  itemSize: itemSizeProp = 12,
  showSearch = false,
  status,
}: DistrictsTemplateProps) => {
  const [searchQuery, setSearchQuery] = useState("");

  return (
    <RenderStates
      data={data}
      status={status}
      empty={<Typography>No districts found</Typography>}
      error={<Typography color="error">Error loading districts</Typography>}
      pending={<CircularProgress />}
      success={(districts) => {
        const q = showSearch ? searchQuery.trim().toLowerCase() : "";
        const list = !q ? districts : districts.filter((d) => d.name.toLowerCase().includes(q));
        const noSearchResults = showSearch && q && list.length === 0;

        if (noSearchResults) {
          return (
            <Box>
              <EntityListSearchField
                id="districts-template-search"
                label="Search districts"
                value={searchQuery}
                onChange={({ target: { value } }) => {
                  setSearchQuery(value);
                }}
              />
              <Typography sx={{ mt: 2 }}>{tNoSearchMatch}</Typography>
            </Box>
          );
        }

        return (
          <Box>
            {showSearch && (
              <EntityListSearchField
                id="districts-template-search"
                label="Search districts"
                sx={{ mb: 2 }}
                value={searchQuery}
                onChange={({ target: { value } }) => {
                  setSearchQuery(value);
                }}
              />
            )}
            <Grid container spacing={2}>
              {list.map((district) => (
                <Grid key={district.id} size={itemSizeProp}>
                  {districtListContent(district)}
                </Grid>
              ))}
            </Grid>
          </Box>
        );
      }}
    />
  );
};
